import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Check, X, Mail, Key, ShieldCheck } from 'lucide-react';
import apiService from '../../services/api';
import {
  AdminPageLayout,
  Button,
  FormInput,
  FormSelect,
  FormTextArea,
  FormCheckbox,
  Section,
  Card,
  CardContent,
  Grid,
  Col,
  ConfirmDialog,
  Badge
} from '../../components';
import {
  FormActionGroup,
  AttributeStack
} from '../../components/admin/patterns';
import PhotoUpload from '../../components/PhotoUpload';
import ProfileInfo from '../../components/ProfileInfo';
import { useFormValidation } from '../../hooks/useFormValidation';

const EditUser = () => {
  const navigate = useNavigate();
  const { userId, branch_id } = useParams();
  const [photoFile, setPhotoFile] = useState(null);

  const [originalData, setOriginalData] = useState({});
  const [availableRoles, setAvailableRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showResetPasswordConfirm, setShowResetPasswordConfirm] = useState(false);
  const [showResendInviteConfirm, setShowResendInviteConfirm] = useState(false);

  // Role filtering based on context
  const BRANCH_ALLOWED_ROLES = ['branch_admin', 'coach', 'parent'];
  const CLUB_ADMIN_ALLOWED_ROLES = ['club_admin', 'branch_admin'];
  const isBranchContext = !!branch_id;

  const {
    formData,
    errors,
    touched,
    isDirty,
    isSubmitting,
    handleFieldChange,
    handleFieldBlur,
    handleSubmit: handleFormSubmit,
    setFormData,
    setFieldValue
  } = useFormValidation({
    initialValues: {
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      role: '',
      status: 'active',
      photo_url: '',
      qualifications: '',
      bio: '',
      enable_public_profile_display: true
    },
    validationSchema: (values) => {
      const newErrors = {};
      if (!values.first_name.trim()) {
        newErrors.first_name = 'First name is required';
      }
      if (!values.last_name.trim()) {
        newErrors.last_name = 'Last name is required';
      }
      if (!values.email.trim()) {
        newErrors.email = 'Email is required';
      } else if (!/\S+@\S+\.\S+/.test(values.email)) {
        newErrors.email = 'Please enter a valid email address';
      }
      if (!values.role) {
        newErrors.role = 'Role is required';
      }
      return newErrors;
    },
    onSubmit: async (values) => {
      const changedData = {};
      Object.keys(values).forEach(key => {
        if (values[key] !== originalData[key]) {
          changedData[key] = values[key] || null;
        }
      });

      if (Object.keys(changedData).length === 0 && !photoFile) {
        const targetPath = isBranchContext ? `/branch/${branch_id}/users` : '/admin/users';
        navigate(targetPath);
        return;
      }

      try {
        if (Object.keys(changedData).length > 0) {
          if (changedData.role) {
            const roleObj = availableRoles.find(r => r.id === changedData.role);
            const canonicalSlug = roleObj?.slug || (roleObj?.name && roleObj.name.trim().toLowerCase().replace(/\s+/g, '_')) || roleObj?.id || changedData.role;
            changedData.primary_role_id = changedData.role;
            changedData.primary_role = canonicalSlug;
            delete changedData.role;
          }

          // Add branch_id if in branch context
          if (branch_id) {
            changedData.branch_id = parseInt(branch_id);
          }

          await apiService.updateUser(userId, changedData);
        }

        if (photoFile) {
          try {
            const photoFormData = new FormData();
            photoFormData.append('file', photoFile);

            const resJson = await apiService.uploadUserPhoto(userId, photoFormData);
            if (resJson && resJson.photo_url) {
              setFormData(prev => ({ ...prev, photo_url: resJson.photo_url }));
            }
            setPhotoFile(null);
          } catch (photoError) {
            console.warn('Photo upload error:', photoError.detail || photoError.message || photoError);
          }
        }

        const targetPath = isBranchContext ? `/branch/${branch_id}/users` : '/admin/users';
        navigate(targetPath, {
          state: {
            message: `User ${values.first_name} ${values.last_name} has been updated successfully!`
          }
        });
      } catch (err) {
        throw err;
      }
    }
  });

  useEffect(() => {
    loadUserData();
    loadAvailableRoles();
  }, [userId, branch_id]); // Add branch_id dependency

  const loadUserData = async () => {
    try {
      setLoading(true);
      const user = await apiService.getUser(userId);

      const userData = {
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        email: user.email || '',
        phone: user.phone || '',
        role: user.primary_role_id || user.primary_role || '',
        status: user.status || 'active',
        photo_url: user.photo_url || '',
        qualifications: user.qualifications || '',
        bio: user.bio || '',
        enable_public_profile_display: user.public_profile_display_status !== 'revoked'
      };

      setFormData(userData);
      setOriginalData(userData);
      setLoading(false);
    } catch (err) {
      setError(err.message || 'Failed to load user data');
      setLoading(false);
    }
  };

  const loadAvailableRoles = async () => {
    try {
      const rolesResponse = await apiService.getClubRoles(false);
      let roles = rolesResponse.roles || rolesResponse || [];
      
      console.log('DEBUG EditUser - Raw roles from API:', roles);
      console.log('DEBUG EditUser - isBranchContext:', isBranchContext);
      
      // Filter roles based on context - always use club-level roles
      if (isBranchContext) {
        // Branch admin: can edit branch_admin, coach, parent
        roles = roles.filter(role => {
          const roleName = (role.name || '').toLowerCase().replace(/\s+/g, '_');
          return BRANCH_ALLOWED_ROLES.includes(roleName);
        });
      } else {
        // Club admin: can edit club_admin, branch_admin
        roles = roles.filter(role => {
          const roleName = (role.name || '').toLowerCase().replace(/\s+/g, '_');
          return CLUB_ADMIN_ALLOWED_ROLES.includes(roleName);
        });
      }
      
      console.log('DEBUG EditUser - Filtered roles:', roles);
      
      // If no roles match after filtering, use defaults
      if (roles.length === 0) {
        console.warn('No matching roles found after filtering. Using default roles for context:', isBranchContext ? 'branch' : 'club');
        if (isBranchContext) {
          roles = [
            { id: 'branch_admin', name: 'Branch Admin', description: 'Manage branch operations' },
            { id: 'coach', name: 'Coach', description: 'Player evaluation and training management' },
            { id: 'parent', name: 'Parent', description: 'View child progress and manage waivers' }
          ];
        } else {
          roles = [
            { id: 'club_admin', name: 'Club Admin', description: 'Full club management access' },
            { id: 'branch_admin', name: 'Branch Admin', description: 'Manage branch operations' }
          ];
        }
      }
      
      setAvailableRoles(roles);
    } catch (err) {
      console.error('Failed to load roles:', err);
      
      // Default roles based on context
      if (isBranchContext) {
        setAvailableRoles([
          { id: 'branch_admin', name: 'Branch Admin', description: 'Manage branch operations' },
          { id: 'coach', name: 'Coach', description: 'Player evaluation and training management' },
          { id: 'parent', name: 'Parent', description: 'View child progress and manage waivers' }
        ]);
      } else {
        setAvailableRoles([
          { id: 'club_admin', name: 'Club Admin', description: 'Full club management access' },
          { id: 'branch_admin', name: 'Branch Admin', description: 'Manage branch operations' }
        ]);
      }
    }
  };

  const handleSubmit = async (e) => {
    try {
      setError(null);
      await handleFormSubmit(e);
    } catch (err) {
      setError(err.message || 'Failed to update user');
    }
  };

  const handleResetPassword = async () => {
    try {
      await apiService.resetUserPassword(userId);
      alert('Password reset email sent successfully!');
      setShowResetPasswordConfirm(false);
    } catch (err) {
      alert(`Failed to send password reset: ${err.message}`);
    }
  };

  const handleResendInvitation = async () => {
    try {
      const result = await apiService.resendUserInvitation(userId);
      alert(result.message || 'Invitation email resent successfully!');
      setShowResendInviteConfirm(false);
    } catch (err) {
      alert(`Failed to resend invitation: ${err.message}`);
    }
  };

  const getRoleDescription = (roleId) => {
    const role = availableRoles.find(r => r.id === roleId);
    return role?.description || '';
  };

  const handlePhotoSelect = (photoData) => {
    setPhotoFile(photoData.file);
  };

  const handlePhotoRemove = () => {
    setPhotoFile(null);
    setFieldValue('photo_url', '');
  };

  const handleQualificationsChange = (value) => {
    setFieldValue('qualifications', value);
  };

  const handleBioChange = (value) => {
    setFieldValue('bio', value);
  };

  const getStatusBadgeVariant = (status) => {
    switch (status) {
      case 'active': return 'success';
      case 'inactive': return 'danger';
      case 'pending': return 'warning';
      default: return 'outline';
    }
  };

  if (loading) {
    return (
      <AdminPageLayout title="SYNCHRONIZING..." maxWidth="1400px">
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '400px', gap: '1.5rem' }}>
          <div className="loading-spinner"></div>
          <p style={{ opacity: 0.6 }}>Synchronizing guardian telemetry...</p>
        </div>
      </AdminPageLayout>
    );
  }

  return (
    <AdminPageLayout
      title="Edit User"
      subtitle={`Editing ${formData.first_name} ${formData.last_name}`}
      backPath={isBranchContext ? `/branch/${branch_id}/users` : '/admin/users'}
      maxWidth="1400px"
      actions={
        <div style={{ display: 'flex', gap: '1rem' }}>
          {formData.status === 'pending' && (
            <Button onClick={() => setShowResendInviteConfirm(true)} variant="outline" size="md">
              <Mail size={16} /> RESEND INVITATION
            </Button>
          )}
          <Button onClick={() => setShowResetPasswordConfirm(true)} variant="secondary" size="md">
            <Key size={16} /> RESET PASSWORD
          </Button>
        </div>
      }
    >
      <form onSubmit={handleSubmit}>
        {error && (
          <Section>
            <div style={{ padding: '1.25rem', background: 'var(--color-error-light)', border: '1px solid var(--color-error)', borderRadius: '12px', color: 'var(--color-error)', textAlign: 'center', fontWeight: 600 }}>
              MODIFICATION BLOCKED: {error}
            </div>
          </Section>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: '3.5rem' }}>
          {/* Status & Identity */}
          <Section title="OPERATIONAL STATUS">
            <Card level={1}>
              <CardContent>
                <div style={{ display: 'flex', alignItems: 'center', gap: '2.5rem' }}>
                  <AttributeStack
                    label="CURRENT STATE"
                    value={<Badge variant={getStatusBadgeVariant(formData.status)}>{formData.status.toUpperCase()}</Badge>}
                  />
                  <AttributeStack
                    label="USER IDENTIFIER"
                    value={<code>{userId}</code>}
                  />
                </div>
              </CardContent>
            </Card>
          </Section>

          <Section title="Profile">
            <Card level={1}>
              <CardContent>
                <Grid columns={12} gap="2.5rem">
                  <Col span={6}>
                    <FormInput
                      label="Given Name"
                      name="first_name"
                      value={formData.first_name}
                      onChange={handleFieldChange}
                      onBlur={handleFieldBlur}
                      error={touched.first_name ? errors.first_name : ''}
                      required
                    />
                  </Col>
                  <Col span={6}>
                    <FormInput
                      label="Family Name"
                      name="last_name"
                      value={formData.last_name}
                      onChange={handleFieldChange}
                      onBlur={handleFieldBlur}
                      error={touched.last_name ? errors.last_name : ''}
                      required
                    />
                  </Col>
                  <Col span={8}>
                    <FormInput
                      label="Authentication Endpoint (Email)"
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleFieldChange}
                      onBlur={handleFieldBlur}
                      error={touched.email ? errors.email : ''}
                      required
                    />
                  </Col>
                  <Col span={4}>
                    <FormInput
                      label="Communication Hotline (Phone)"
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleFieldChange}
                      onBlur={handleFieldBlur}
                      error={touched.phone ? errors.phone : ''}
                    />
                  </Col>
                </Grid>
              </CardContent>
            </Card>
          </Section>

          {/* Authorization */}
          <Section title="AUTHORIZATION LEVEL">
            <Card level={1}>
              <CardContent>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
                  <FormSelect
                    label="Assigned System Role"
                    name="role"
                    value={formData.role}
                    onChange={handleFieldChange}
                    onBlur={handleFieldBlur}
                    error={touched.role ? errors.role : ''}
                    required
                    options={[
                      { value: '', label: 'Select protocol level...' },
                      ...availableRoles.map(role => ({ value: role.id, label: role.name }))
                    ]}
                  />
                  {formData.role && (
                    <div style={{ background: 'var(--surface-lvl2)', padding: '1.5rem', borderRadius: '12px', border: '1px solid var(--color-border-light)' }}>
                      <AttributeStack
                        label="ROLE CAPABILITIES"
                        value={getRoleDescription(formData.role)}
                        variant="small"
                      />
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </Section>

          {/* Curated Background */}
          <Section title="Biography & Public Profile">
            <Card level={1}>
              <CardContent>
                <Grid columns={12} gap="3.5rem">
                  <Col span={4}>
                    <PhotoUpload
                      onPhotoSelect={handlePhotoSelect}
                      onPhotoRemove={handlePhotoRemove}
                      photoUrl={formData.photo_url}
                      label="BIOMETRIC IDENTIFIER (PHOTO)"
                      hint="JPG/PNG up to 5MB"
                    />
                  </Col>
                  <Col span={8}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '2.5rem' }}>
                      <ProfileInfo
                        qualifications={formData.qualifications}
                        bio={formData.bio}
                        onQualificationsChange={handleQualificationsChange}
                        onBioChange={handleBioChange}
                        errors={errors}
                        touched={touched}
                        onBlur={handleFieldBlur}
                      />

                      <div style={{ background: 'var(--surface-lvl2)', padding: '1.75rem', borderRadius: '16px', border: '1px solid var(--color-border-light)' }}>
                        <FormCheckbox
                          label="Authorize Public Visibility"
                          name="enable_public_profile_display"
                          checked={formData.enable_public_profile_display}
                          onChange={handleFieldChange}
                          helpText="Guardian must consent before public appearance."
                        />
                      </div>
                    </div>
                  </Col>
                </Grid>
              </CardContent>
            </Card>
          </Section>

          <FormActionGroup
            onCancel={() => navigate(isBranchContext ? `/branch/${branch_id}/users` : '/admin/users')}
            onSave={handleSubmit}
            loading={isSubmitting}
            saveLabel="Save Changes"
            cancelLabel="Cancel"
          />
        </div>
      </form>

      <ConfirmDialog
        isOpen={showResetPasswordConfirm}
        onClose={() => setShowResetPasswordConfirm(false)}
        onConfirm={handleResetPassword}
        title="Reset Password"
        message={`Send password reset email to ${formData.email}?`}
        details="This will send them a link to create a new password."
        variant="info"
        confirmText="Send Reset Email"
        cancelText="Cancel"
      />

      <ConfirmDialog
        isOpen={showResendInviteConfirm}
        onClose={() => setShowResendInviteConfirm(false)}
        onConfirm={handleResendInvitation}
        title="REISSUE INVITATION"
        message={`Resend authorization link to ${formData.email}?`}
        details="A new high-entropy invitation link will be dispatched immediately."
        variant="info"
        confirmText="Reissue Now"
        cancelText="Cancel"
      />
    </AdminPageLayout>
  );
};

export default EditUser;
